import { U as gosNREUM, F as FEATURE_NAMES, p as getRuntime, x as originTime, V as FEATURE_NAME, m as warn, i as isBrowserScope, s as getInfo, b as globalScope, G as now, n as handle, o as SUPPORTABILITY_METRIC_CHANNEL, u as drain, W as activateFeatures } from './micro-agent-8d9a9dc2.js';
import { a as addPT, b as addPN } from './nav-timing-54f776f3.js';
import { s as stringify } from './stringify-d8602ca9.js';
import { A as AggregateBase, a as isConfigured, H as Harvest } from './aggregate-base-619aa2ce.js';
import { f as firstPaint, a as firstContentfulPaint } from './first-paint-f9c84220.js';
import { t as timeToFirstByte } from './time-to-first-byte-8d7da3ad.js';

/**
 * Get an array of flags required by downstream (NR UI) based on the features initialized in this agent
 * (aka what is running on the page).
 * @param {String} agentId - the ID of the initialized agent on the page, mapping to the one under the global 'newrelic' object
 * @returns {String[]} Up to 5 short strings corresponding to ingest mapping of features.
 */
function getActivatedFeaturesFlags(agentId) {
  const flagArr = [];
  const newrelic = gosNREUM();
  try {
    Object.keys(newrelic.initializedAgents[agentId].features).forEach(featName => {
      switch (featName) {
        case FEATURE_NAMES.ajax:
          flagArr.push('xhr');
          break;
        case FEATURE_NAMES.jserrors:
          flagArr.push('err');
          break;
        case FEATURE_NAMES.pageAction:
          flagArr.push('ins');
          break;
        case FEATURE_NAMES.sessionTrace:
          flagArr.push('stn');
          break;
        case FEATURE_NAMES.softNav:
        case FEATURE_NAMES.spa:
          flagArr.push('spa');
          break;
      }
    });
  } catch (e) {}
  return flagArr;
}

// Note: this module and the "af" param in src/features/page_view_event/aggregate/index.js can be removed in the future at such time
// that it's no longer being used. For the browser agent, this is an unused flag system.

const rfc2616Regex = /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun), ([0-3][0-9]) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) ([0-9]{4}) ([01][0-9]|2[0-3])(:[0-5][0-9]){2} GMT$/;

/**
 * Class used to adjust the timestamp of harvested data to New Relic server time. This
 * is done by tracking the performance timings of the RUM call and applying a calculation
 * to the harvested data event offset time.
 */
class TimeKeeper {
  /**
   * Pointer to the current agent session if it exists.
   * @type {import('../session/session-entity').SessionEntity}
   */
  #session;

  /**
   * Represents the browser origin time corrected to NR server time.
   * @type {number}
   */
  #correctedOriginTime;

  /**
   * Represents the difference in milliseconds between the calculated NR server time and
   * the local time.
   * @type {number}
   */
  #localTimeDiff;

  /**
   * Represents whether the timekeeper is in a state that it can accurately convert
   * timestamps.
   * @type {boolean}
   */
  #ready = false;
  constructor(agentIdentifier) {
    this.#session = getRuntime(agentIdentifier)?.session;
    this.processStoredDiff();
  }
  get ready() {
    return this.#ready;
  }
  get correctedOriginTime() {
    return this.#correctedOriginTime;
  }
  get localTimeDiff() {
    return this.#localTimeDiff;
  }

  /**
   * Process a rum request to calculate NR server time.
   * @param rumRequest {XMLHttpRequest} The xhr for the rum request
   * @param startTime {number} The start time of the RUM request
   * @param endTime {number} The end time of the RUM request
   */
  processRumRequest(rumRequest, startTime, endTime) {
    this.processStoredDiff(); // Check session entity for stored time diff
    if (this.#ready) return; // Server time calculated from session entity

    const responseDateHeader = rumRequest.getResponseHeader('Date');
    if (!responseDateHeader) {
      throw new Error('Missing date header on rum response.');
    }
    if (!rfc2616Regex.test(responseDateHeader)) {
      throw new Error('Date header invalid format.');
    }
    const medianRumOffset = (endTime - startTime) / 2;
    const serverOffset = startTime + medianRumOffset;

    // Corrected page origin time
    this.#correctedOriginTime = Math.floor(Date.parse(responseDateHeader) - serverOffset);
    this.#localTimeDiff = originTime - this.#correctedOriginTime;
    if (isNaN(this.#correctedOriginTime)) {
      throw new Error('Date header invalid format.');
    }
    this.#session?.write({
      serverTimeDiff: this.#localTimeDiff
    });
    this.#ready = true;
  }

  /**
   * Converts a page origin relative time to an absolute timestamp
   * corrected to NR server time.
   * @param relativeTime {number} The relative time of the event in milliseconds
   * @returns {number} Corrected unix/epoch timestamp
   */
  convertRelativeTimestamp(relativeTime) {
    return Math.floor(this.#correctedOriginTime + relativeTime);
  }

  /**
   * Corrects an event timestamp to NR server time.
   * @param timestamp {number} The unix/epoch timestamp of the event with milliseconds
   * @return {number} Corrected unix/epoch timestamp
   */
  correctAbsoluteTimestamp(timestamp) {
    return Math.floor(timestamp - this.#localTimeDiff);
  }

  /** Process the session entity and use the info to set the main time calculations if present */
  processStoredDiff() {
    if (this.#ready) return; // Time diff has already been calculated

    const storedServerTimeDiff = this.#session?.read()?.serverTimeDiff;
    if (typeof storedServerTimeDiff === 'number' && !isNaN(storedServerTimeDiff)) {
      this.#localTimeDiff = storedServerTimeDiff;
      this.#correctedOriginTime = originTime - this.#localTimeDiff;
      this.#ready = true;
    }
  }
}

class Aggregate extends AggregateBase {
  static featureName = FEATURE_NAME;
  constructor(agentIdentifier, aggregator) {
    super(agentIdentifier, aggregator, FEATURE_NAME);
    this.timeToFirstByte = 0;
    this.firstByteToWindowLoad = 0; // our "frontend" duration
    this.firstByteToDomContent = 0; // our "dom processing" duration
    this.timeKeeper = new TimeKeeper(this.agentIdentifier);
    if (!isConfigured(agentIdentifier)) {
      this.ee.abort();
      return warn(43);
    }
    if (isBrowserScope) {
      timeToFirstByte.subscribe(_ref => {
        let {
          value,
          attrs
        } = _ref;
        const navEntry = attrs.navigationEntry;
        this.timeToFirstByte = Math.max(value, this.timeToFirstByte);
        this.firstByteToWindowLoad = Math.max(Math.round(navEntry.loadEventEnd - this.timeToFirstByte), this.firstByteToWindowLoad); // our "frontend" duration
        this.firstByteToDomContent = Math.max(Math.round(navEntry.domContentLoadedEventEnd - this.timeToFirstByte), this.firstByteToDomContent); // our "dom processing" duration

        this.sendRum();
      });
    } else {
      // worker agent build does not get TTFB values, use default 0 values
      this.sendRum();
    }
  }
  sendRum() {
    const info = getInfo(this.agentIdentifier);
    const agentRuntime = getRuntime(this.agentIdentifier);
    const harvester = new Harvest(this);
    if (info.queueTime) this.aggregator.store('measures', 'qt', {
      value: info.queueTime
    });
    if (info.applicationTime) this.aggregator.store('measures', 'ap', {
      value: info.applicationTime
    });

    // These 3 values should've been recorded after load and before this func runs. They are part of the minimum required for PageView events to be created.
    // Following PR #428, which demands that all agents send RUM call, these need to be sent even outside of the main window context where PerformanceTiming
    // or PerformanceNavigationTiming do not exists. Hence, they'll be filled in by 0s instead in, for example, worker threads that still init the PVE module.
    this.aggregator.store('measures', 'be', {
      value: this.timeToFirstByte
    });
    this.aggregator.store('measures', 'fe', {
      value: this.firstByteToWindowLoad
    });
    this.aggregator.store('measures', 'dc', {
      value: this.firstByteToDomContent
    });
    const queryParameters = {
      tt: info.ttGuid,
      us: info.user,
      ac: info.account,
      pr: info.product,
      af: getActivatedFeaturesFlags(this.agentIdentifier).join(','),
      ...Object.entries(this.aggregator.get('measures') || {}).reduce((aggregator, _ref2) => {
        let [metricName, measure] = _ref2;
        aggregator[metricName] = measure.params?.value;
        return aggregator;
      }, {}),
      xx: info.extra,
      ua: info.userAttributes,
      at: info.atts
    };
    if (agentRuntime.session) queryParameters.fsh = Number(agentRuntime.session.isNew); // "first session harvest" aka RUM request or PageView event of a session

    let body;
    if (typeof info.jsAttributes === 'object' && Object.keys(info.jsAttributes).length > 0) {
      body = {
        ja: info.jsAttributes
      };
    }
    if (globalScope.performance) {
      if (typeof PerformanceNavigationTiming !== 'undefined') {
        // Navigation Timing level 2 API that replaced PerformanceTiming & PerformanceNavigation
        const navTimingEntry = globalScope?.performance?.getEntriesByType('navigation')?.[0];
        const perf = {
          timing: addPT(originTime, navTimingEntry, {}),
          navigation: addPN(navTimingEntry, {})
        };
        queryParameters.perf = stringify(perf);
      } else if (typeof PerformanceTiming !== 'undefined') {
        // Safari pre-15 did not support level 2 timing
        const perf = {
          timing: addPT(originTime, globalScope.performance.timing, {}, true),
          navigation: addPN(globalScope.performance.navigation, {})
        };
        queryParameters.perf = stringify(perf);
      }
    }
    queryParameters.fp = firstPaint.current.value;
    queryParameters.fcp = firstContentfulPaint.current.value;
    if (this.timeKeeper?.ready) {
      queryParameters.timestamp = this.timeKeeper.convertRelativeTimestamp(now());
    }
    const rumStartTime = now();
    harvester.send({
      endpoint: 'rum',
      payload: {
        qs: queryParameters,
        body
      },
      opts: {
        needResponse: true,
        sendEmptyBody: true
      },
      cbFinished: _ref3 => {
        let {
          status,
          responseText,
          xhr
        } = _ref3;
        const rumEndTime = now();
        if (status >= 400 || status === 0) {
          // Adding retry logic for the rum call will be a separate change
          this.ee.abort();
          return;
        }
        try {
          this.timeKeeper.processRumRequest(xhr, rumStartTime, rumEndTime);
          if (!this.timeKeeper.ready) throw new Error('TimeKeeper not ready');
          agentRuntime.timeKeeper = this.timeKeeper;

          // Check if the time diff is such that we need to capture a supportability metric
          if (this.timeKeeper.localTimeDiff >= 12 * 60 * 60 * 1000) {
            handle(SUPPORTABILITY_METRIC_CHANNEL, ['PVE/NRTime/Calculation/DiffExceed12Hrs'], undefined, FEATURE_NAMES.metrics, this.ee);
          } else if (this.timeKeeper.localTimeDiff >= 6 * 60 * 60 * 1000) {
            handle(SUPPORTABILITY_METRIC_CHANNEL, ['PVE/NRTime/Calculation/DiffExceed6Hrs'], undefined, FEATURE_NAMES.metrics, this.ee);
          } else if (this.timeKeeper.localTimeDiff >= 60 * 60 * 1000) {
            handle(SUPPORTABILITY_METRIC_CHANNEL, ['PVE/NRTime/Calculation/DiffExceed1Hrs'], undefined, FEATURE_NAMES.metrics, this.ee);
          }
        } catch (error) {
          if (error?.message?.indexOf('invalid format') > 0) {
            handle(SUPPORTABILITY_METRIC_CHANNEL, ['PVE/NRTime/Calculation/InvalidFormat'], undefined, FEATURE_NAMES.metrics, this.ee);
          } else {
            handle(SUPPORTABILITY_METRIC_CHANNEL, ['PVE/NRTime/Calculation/Failed'], undefined, FEATURE_NAMES.metrics, this.ee);
          }
          drain(this.agentIdentifier, FEATURE_NAMES.metrics, true);
          this.ee.abort();
          warn(17, error);
          return;
        }
        try {
          const {
            app,
            ...flags
          } = JSON.parse(responseText);
          agentRuntime.appMetadata = app;
          activateFeatures(flags, this.agentIdentifier);
          this.drain();
        } catch (err) {
          this.ee.abort();
          warn(18, err);
        }
      }
    });
  }
}

export { Aggregate };
//# sourceMappingURL=index-d6f4755e.js.map
